import {
  type PropsWithChildren,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { filter, fromEvent, map, of, switchMap, tap } from 'rxjs';

import { useCustomEventEmitter, useSubscriptions } from '@/hook';

import type { VGridProps } from '../VGrid';
import { joinCn } from '../util';
import { TABLE_ROW_SIZE_HEIGHT } from './constant';
import VBaseTableContext from './context';
import { useDefaultColumnList } from './hook';
import type {
  VBaseTableColumnStyleMap,
  VBaseTableContextValue,
  VBaseTableData,
  VBaseTableProps,
} from './type';

const VBaseTableContextProvider = <T extends VBaseTableData>({
  children,
  ...rest
}: PropsWithChildren<VBaseTableProps<T>>) => {
  const {
    columnList: _columnList,
    columnStyleMap: _columnStyleMap,
    copyableColumnList,
    data,
    rowSize = 'md',
    onCellClick,
    onCopy,
    onDoubleClick,
    onRowClick,
    onScrollEnd,
  } = rest;
  const columnList = useDefaultColumnList(_columnList, data);
  const [columnWidthMap, setColumnWidthMap] = useState<Dictionary<number>>({});
  const copyableColumnMap = useMemo(
    () =>
      copyableColumnList?.reduce(
        (acc, cur) => ({
          ...acc,
          [cur]: true,
        }),
        {} as {
          [K in UnwrapArray<VBaseTableProps<T>['copyableColumnList']>]: true;
        },
      ),
    [copyableColumnList],
  );

  const columnStyleMap = useMemo(() => {
    const columnStyleMap = {} as Dictionary<any>;

    columnList.forEach(col => {
      const colProp = _columnStyleMap?.[col] ?? {};
      const baseStyle = {
        ...colProp.style,
        width: null,
        minWidth: null,
        maxWidth: null,
      };

      columnStyleMap[col] = {
        className: joinCn(colProp.className).replace(
          /((max|min)-)?w-(\d+|\[\d+[a-z]+\]|\([-_a-z0-9]+\))/gi,
          '',
        ),
        style: baseStyle,
      };
    });

    return columnStyleMap as VBaseTableColumnStyleMap<T>;
  }, [_columnStyleMap, columnList]);
  const colsWidth = useMemo(() => {
    const columnLength = columnList.length;
    const colsWidth = columnList.map(col => {
      const defaultWidth = `${100 / columnLength}%`;
      const width = columnWidthMap[col];
      const colProp = _columnStyleMap?.[col] ?? {};
      const styleWidth = colProp.style?.width;
      const className = joinCn(colProp.className)
        .match(/((max|min)-)?(?!w-)(\d+|\[\d+[a-z]+\]|\([-_a-z0-9]+\))/gi)?.[0]
        .replace(/\(|\)|\[|\]/gi, '');
      const classNameWidth: string = className
        ? className.startsWith('--')
          ? (colProp.style as any)?.[className]
          : className.match(/[a-z]+$/)
            ? className
            : `${+className * 4}px`
        : '';

      return width
        ? `${width}px`
        : (styleWidth ?? classNameWidth ?? defaultWidth);
    });

    return colsWidth as VGridProps['colsWidth'];
  }, [_columnStyleMap, columnList, columnWidthMap]);
  const { eventEmitter } = useCustomEventEmitter({
    onCellClick: onCellClick ?? (() => {}),
    onCopy: onCopy ?? (() => {}),
    onDoubleClick: onDoubleClick ?? (() => {}),
    onRowClick: onRowClick ?? (() => {}),
    onScrollEnd: onScrollEnd ?? (() => {}),
  });
  const [hasScroll, setHasScroll] = useState(false);
  const [visibleTheadShadow, setVisibleTheadShadow] = useState(false);
  const $table = useRef<HTMLDivElement>(null);
  const $tbody = useRef<HTMLDivElement>(null);
  const $thead = useRef<HTMLDivElement>(null);

  const handleColumnResize = (column: keyof T & string, width: number) => {
    setColumnWidthMap(prev => ({
      ...prev,
      [column]: width,
    }));
  };

  useLayoutEffect(() => {
    const tbody = $tbody.current;

    if (!tbody) {
      setHasScroll(false);
      return;
    }

    // TODO: VVirtualScroll 의 높이 조절 로직 이후에 실행 되어야 하는데 현재 그 로직 이후의 동작을 제어할 hook 이나 이벤트가 없어서 setTimeout 으로 임시 대체
    setTimeout(() => setHasScroll(tbody.scrollHeight > tbody.offsetHeight), 10);
  }, [columnStyleMap, data, rowSize]);

  useSubscriptions(
    () => [
      of($tbody.current)
        .pipe(
          map(() => $tbody.current as HTMLDivElement),
          filter(tbody => !!tbody && !!$thead.current),
          switchMap(tbody => {
            return fromEvent(tbody, 'scroll').pipe(
              map(() => tbody.scrollTop),
              tap(scrollTop => {
                setVisibleTheadShadow(scrollTop >= 10);

                if (
                  tbody.scrollHeight - tbody.offsetHeight - scrollTop <=
                  TABLE_ROW_SIZE_HEIGHT[rowSize]
                ) {
                  eventEmitter.current.emit('scrollEnd', null);
                }
              }),
            );
          }),
        )
        .subscribe(),
    ],
    [data],
  );

  return (
    <VBaseTableContext.Provider<VBaseTableContextValue<T>>
      value={{
        ...rest,
        colsWidth,
        columnList,
        columnStyleMap,
        columnWidthMap,
        copyableColumnMap,
        data,
        eventEmitter: eventEmitter.current,
        hasScroll,
        rowSize,
        visibleTheadShadow,
        $table,
        $tbody,
        $thead,
        handleColumnResize,
      }}
    >
      {children}
    </VBaseTableContext.Provider>
  );
};

export default VBaseTableContextProvider;
